/**
 * Meta Pixel integration utility.
 * Supports global pixel ID (from env/site config) and per-page override.
 *
 * Usage:
 *   import { initMetaPixel, trackEvent } from "../lib/metaPixel";
 *   initMetaPixel("123456789"); // injects pixel script + fires PageView
 *   trackEvent("AddToCart", { value: 50000, currency: "IDR" });
 */

let initialized = false;
let currentPixelId = null;

const META_PIXEL_SCRIPT_ID = "meta-pixel-base-script";
const META_PIXEL_SRC = "https://connect.facebook.net/en_US/fbevents.js";

function normalizePixelId(pixelId) {
  if (!pixelId) return "";
  return String(pixelId).replace(/\D/g, "");
}

function markActivePixel(pixelId) {
  if (typeof document === "undefined") return;
  if (pixelId) {
    document.documentElement.setAttribute("data-meta-pixel-id", pixelId);
    window.__ACTIVE_META_PIXEL_ID__ = pixelId;
    return;
  }
  document.documentElement.removeAttribute("data-meta-pixel-id");
  delete window.__ACTIVE_META_PIXEL_ID__;
}

/**
 * Initialize Meta Pixel by injecting the fbq script.
 * Safe to call multiple times — will only inject once per pixel ID.
 */
export function initMetaPixel(pixelId) {
  const normalizedPixelId = normalizePixelId(pixelId);
  if (!normalizedPixelId || typeof window === "undefined") return;

  // Already initialized with same pixel
  if (initialized && currentPixelId === normalizedPixelId) {
    markActivePixel(normalizedPixelId);
    return;
  }

  // If switching pixel (e.g., different LP), reset
  if (initialized && currentPixelId !== normalizedPixelId) {
    // fbq can handle multiple pixels via fbq('init', newId)
    if (window.fbq) {
      window.fbq("init", normalizedPixelId);
      window.fbq("track", "PageView");
      currentPixelId = normalizedPixelId;
      markActivePixel(normalizedPixelId);
      return;
    }
  }

  // First-time initialization
  if (!window.fbq) {
    const fbq = function () {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];

    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
  }

  if (!document.getElementById(META_PIXEL_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = META_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = META_PIXEL_SRC;
    script.onerror = () => {
      console.warn("Meta Pixel gagal dimuat dari connect.facebook.net");
    };
    document.head.appendChild(script);
  }

  window.fbq("init", normalizedPixelId);
  window.fbq("track", "PageView");

  initialized = true;
  currentPixelId = normalizedPixelId;
  markActivePixel(normalizedPixelId);
}

/**
 * Ensure pixel is initialized — uses current pixel or global fallback.
 * Call this on pages that need to fire events but may not have initialized pixel themselves.
 */
export function ensurePixelReady() {
  // Already initialized from a previous page (SPA navigation)
  if (initialized && currentPixelId && window.fbq) return true;

  // Try global pixel ID as fallback
  const globalId = getGlobalPixelId();
  if (globalId) {
    initMetaPixel(globalId);
    return true;
  }

  // Check if fbq exists (maybe initialized by LP page earlier in session)
  if (typeof window !== "undefined" && window.fbq) return true;

  return false;
}

/**
 * Get the currently active pixel ID (per-LP or global).
 */
export function getActivePixelId() {
  return currentPixelId || getGlobalPixelId() || "";
}

/**
 * Track a standard or custom Meta Pixel event.
 * Will auto-initialize pixel if not yet done (using stored or global pixel ID).
 * @param {string} eventName - e.g. "AddToCart", "Purchase", "Lead", "ViewContent"
 * @param {object} params - event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (typeof window === "undefined") return;

  // Auto-ensure pixel is ready before tracking
  if (!window.fbq) {
    ensurePixelReady();
  }

  if (window.fbq) {
    window.fbq("track", eventName, params);
  }
}

/**
 * Track a custom event (non-standard).
 * @param {string} eventName
 * @param {object} params
 */
export function trackCustomEvent(eventName, params = {}) {
  if (typeof window === "undefined") return;
  if (!window.fbq) ensurePixelReady();
  if (window.fbq) {
    window.fbq("trackCustom", eventName, params);
  }
}

/**
 * Get the global Meta Pixel ID from environment variable.
 * Falls back to hardcoded pixel ID for production.
 */
export function getGlobalPixelId() {
  if (typeof process !== "undefined" && process.env?.REACT_APP_META_PIXEL_ID) {
    return normalizePixelId(process.env.REACT_APP_META_PIXEL_ID);
  }
  // Hardcoded fallback for production — ensures pixel always works
  // even if env variable is not properly injected during build
  return "1273454404565994";
}
